import { useNavigate, Link } from 'react-router-dom';
import type { PackMetadataInputT } from '@skey/manifest';
import PackMetadataForm from '../components/PackMetadataForm';
import { ApiError } from '../lib/api';
import { createPack } from '../lib/packs';
import { useToastStore } from '../components/Toast';

/**
 * `/packs/new` — create-pack form.
 *
 * The form lives in the shared `<PackMetadataForm>` so we get the same
 * validation and field set as the editor (Requirement 22.1). Only the
 * fields the Worker currently consumes (`kind`, `name`, `description`)
 * are sent on submit; `coverImage` and `tags` stay client-side because
 * `POST /admin/v1/packs` rejects unknown keys (see Worker
 * `routes/admin/packs.ts#readCreateBody`). When the Worker grows
 * support, the same form populates them automatically.
 *
 * On success we redirect to `/packs/{id}` so the admin lands directly
 * in the editor and can start uploading assets (Requirement 22.2).
 */
export default function NewPackPage(): JSX.Element {
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);

  const handleSubmit = async (values: PackMetadataInputT): Promise<void> => {
    try {
      const created = await createPack({
        kind: values.kind,
        name: values.name,
        description: values.description?.length ? values.description : undefined,
        priceVnd: values.priceVnd ?? 0,
      });
      addToast({
        severity: 'success',
        title: 'Đã tạo pack',
        message: `Đã tạo pack ${created.name}`,
      });
      navigate(`/packs/${encodeURIComponent(created.id)}`, { replace: true });
    } catch (err) {
      // `apiFetch` already pushed an error toast; we just stay on the
      // page so the admin can correct + retry. Unknown errors fall
      // through to the global handler.
      if (!(err instanceof ApiError)) {
        throw err;
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-6 py-8">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Tạo pack mới
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Tạo bản nháp, sau đó tải asset và phát hành ở trang chỉnh sửa.
            </p>
          </div>
          <Link
            to="/packs"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            ← Danh sách pack
          </Link>
        </header>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <PackMetadataForm
            submitLabel="Tạo pack"
            onSubmit={handleSubmit}
            onCancel={() => navigate('/packs')}
          />
        </div>
      </div>
    </div>
  );
}
